import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LayoutDashboard, Stethoscope, Shield, Mail, Lock, LogIn, Search, CheckCircle, XCircle, LogOut } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import AdminDashboard from "../components/admin/AdminDashboard";
import AdminDoctors from "../components/admin/AdminDoctors";
import { useAuth } from "../context/AuthContext";
import { useAuthApi } from "../hooks/useAuthApi";
import { useAdminApi } from "../hooks/useAdminApi";
import { useToast } from "@/hooks/use-toast";

const decodeJWT = (token) => {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(atob(base64).split("").map(c =>
      "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2)
    ).join("")));
  } catch {
    return null;
  }
};

const AdminLoginForm = () => {
  const { login: authLogin } = useAuth();
  const { login: loginUser, loading: apiLoading } = useAuthApi();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const response = await loginUser({ email, password });
      const accessToken = response?.data?.access_token;
      if (!accessToken) throw new Error("No token received");

      const decoded = decodeJWT(accessToken);
      if (!decoded?.roles?.includes("admin")) {
        setError("Access denied. Admin credentials required.");
        return;
      }

      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("token_type", response.data.token_type || "bearer");
      localStorage.setItem("user_first_name", decoded.first_name || "Admin");
      localStorage.setItem("user_id", decoded.sub);
      localStorage.setItem("user_roles", JSON.stringify(decoded.roles));
      localStorage.setItem("user_name", decoded.first_name || "Admin");

      authLogin({
        id: decoded.sub,
        first_name: decoded.first_name || "Admin",
        email,
        roles: decoded.roles,
      });

      toast({ title: "Welcome, Admin", duration: 2000 });
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const isLoading = submitting || apiLoading;

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-orange-600 rounded-2xl mb-4 shadow-md">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Admin Access</h2>
          <p className="text-gray-500 text-sm mt-1">Sign in with your admin credentials</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
          <div className="space-y-1">
            <Label htmlFor="admin-email" className="flex items-center gap-2 text-gray-700">
              <Mail className="w-4 h-4" /> Email
            </Label>
            <Input
              id="admin-email"
              type="email"
              placeholder="admin@geeth365.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="username"
              required
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="admin-password" className="flex items-center gap-2 text-gray-700">
              <Lock className="w-4 h-4" /> Password
            </Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Signing in...
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <LogIn className="w-4 h-4" /> Sign In
              </div>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

const VerifySubscription = () => {
  const { verifySubscription } = useAdminApi();
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await verifySubscription(query.trim());
      setResult(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg space-y-6">
      <p className="text-sm text-gray-500">Enter a User ID or Subscription ID to check status.</p>
      <form onSubmit={handleVerify} className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="User ID or Subscription ID"
          disabled={loading}
          className="flex-1"
        />
        <Button type="submit" disabled={loading || !query.trim()} className="bg-orange-600 hover:bg-orange-700">
          <Search className="w-4 h-4 mr-1" />
          {loading ? "Checking..." : "Verify"}
        </Button>
      </form>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      {result && (
        <Card className={result.is_subscribed ? "border-green-200" : "border-gray-200"}>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              {result.is_subscribed ? (
                <CheckCircle className="w-8 h-8 text-green-600" />
              ) : (
                <XCircle className="w-8 h-8 text-gray-400" />
              )}
              <div>
                <p className="text-lg font-semibold text-gray-900">
                  {result.is_subscribed ? "Subscribed" : "Not Subscribed"}
                </p>
                {result.user_id && (
                  <p className="text-sm text-gray-500">User ID: {result.user_id}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs mb-1">Plan</p>
                <p className="font-medium text-gray-900">
                  {result.subscription_type || "None"}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500 text-xs mb-1">Discount</p>
                <p className="font-semibold text-green-700 text-lg">
                  {result.discount != null ? `${Math.round(result.discount * 100)}%` : "0%"}
                </p>
              </div>
              {result.subscribed_at && (
                <div className="bg-gray-50 rounded-lg p-3 col-span-2">
                  <p className="text-gray-500 text-xs mb-1">Subscribed At</p>
                  <p className="font-medium text-gray-900">
                    {new Date(result.subscribed_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            <Badge className={result.is_subscribed
              ? "bg-green-100 text-green-700 border-green-200"
              : "bg-gray-100 text-gray-600 border-gray-200"
            }>
              {result.is_subscribed
                ? `Eligible for ${Math.round(result.discount * 100)}% discount`
                : "Non-subscriber — 15% discount applies"}
            </Badge>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const Admin = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const isAdmin = isAuthenticated && Array.isArray(user?.roles) && user.roles.includes("admin");

  const handleLogout = () => {
    ["access_token", "token_type", "user_first_name", "user_name",
      "user_id", "user_roles", "geeth365_user"].forEach(k => localStorage.removeItem(k));
    logout();
  };

  if (!isAdmin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AdminLoginForm />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin</h1>
          <p className="text-gray-500 mt-1">Welcome, {user?.first_name || "Admin"}</p>
        </div>
        <Button variant="outline" onClick={handleLogout} className="text-red-600 border-red-200 hover:bg-red-50">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="mb-6">
          <TabsTrigger value="dashboard" className="flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="doctors" className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4" />
            Doctors
          </TabsTrigger>
          <TabsTrigger value="verify" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Verify Subscription
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <AdminDashboard />
        </TabsContent>

        <TabsContent value="doctors">
          <AdminDoctors />
        </TabsContent>

        <TabsContent value="verify">
          <VerifySubscription />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Admin;
